import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateProgressUpdateInput, ShareLocationInput } from '@teentack/shared';
import {
  addProgress,
  fetchLocation,
  fetchProgress,
  shareLocation,
  stopSharingLocation,
} from './worksite.api';

export const worksiteKeys = {
  all: ['worksite'] as const,
  progress: (assignmentId: string) => ['worksite', 'progress', assignmentId] as const,
  location: (assignmentId: string) => ['worksite', 'location', assignmentId] as const,
};

export function useProgress(assignmentId: string | undefined) {
  return useQuery({
    queryKey: worksiteKeys.progress(assignmentId ?? ''),
    queryFn: () => fetchProgress(assignmentId as string),
    enabled: Boolean(assignmentId),
  });
}

export function useAddProgress(assignmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProgressUpdateInput) => addProgress(assignmentId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: worksiteKeys.progress(assignmentId) });
    },
  });
}

/**
 * Where the teen is, polled.
 *
 * Thirty seconds while someone is actually watching the page, and nothing at
 * all otherwise. Without a realtime transport this is the honest trade: often
 * enough to be useful on a job, rare enough not to drain a phone battery.
 */
export function useLocation(assignmentId: string | undefined, watching = false) {
  return useQuery({
    queryKey: worksiteKeys.location(assignmentId ?? ''),
    queryFn: () => fetchLocation(assignmentId as string),
    enabled: Boolean(assignmentId),
    refetchInterval: watching && assignmentId ? 30_000 : false,
  });
}

export function useShareLocation(assignmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ShareLocationInput) => shareLocation(assignmentId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: worksiteKeys.location(assignmentId) });
    },
  });
}

export function useStopSharingLocation(assignmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => stopSharingLocation(assignmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: worksiteKeys.location(assignmentId) });
    },
  });
}
